import { ref, computed, watch, onMounted } from 'vue'
import { safeGet, safeSet } from '@/lib/safeStorage'

const STORAGE_KEY = 'hudstore-theme'
const DARK_CLASS = 'app-dark'

// Global reactive state
const isDark = ref(false)

export function useTheme() {
  // Computed properties
  const theme = computed(() => isDark.value ? 'dark' : 'light')
  
  // Initialize theme
  const initTheme = () => {
    // Check for saved preference
    const savedTheme = safeGet(STORAGE_KEY)
    
    if (savedTheme) {
      isDark.value = savedTheme === 'dark'
    } else {
      // Check system preference
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    
    applyTheme()
  }
  
  // Apply theme to DOM
  const applyTheme = () => {
    const rootElement = document.documentElement
    
    if (isDark.value) {
      rootElement.classList.add(DARK_CLASS)
    } else {
      rootElement.classList.remove(DARK_CLASS)
    }
    
    // Update meta theme-color for mobile browsers
    updateMetaThemeColor()
  }
  
  // Update meta theme-color
  const updateMetaThemeColor = () => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    // Literal values: <meta theme-color> is read by the OS browser chrome and
    // does NOT resolve CSS vars. Mirror --app-background (true black / warm stone).
    const color = isDark.value ? '#000000' : '#FAFAF9'

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', color)
    } else {
      const meta = document.createElement('meta')
      meta.name = 'theme-color'
      meta.content = color
      document.head.appendChild(meta)
    }
  }
  
  // Toggle theme
  const toggleTheme = () => {
    isDark.value = !isDark.value
  }
  
  // Set specific theme
  const setTheme = (darkMode: boolean) => {
    isDark.value = darkMode
  }
  
  // Set theme based on system preference
  const setSystemTheme = () => {
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  
  // Watch for changes and persist
  watch(isDark, (newValue) => {
    safeSet(STORAGE_KEY, newValue ? 'dark' : 'light')
    applyTheme()
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('theme-changed', {
      detail: { theme: newValue ? 'dark' : 'light', isDark: newValue }
    }))
  }, { immediate: false })
  
  // Listen for system theme changes
  const watchSystemTheme = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if no user preference is saved
      if (!safeGet(STORAGE_KEY)) {
        isDark.value = e.matches
      }
    }
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange)
    }
    
    // Return cleanup function
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange)
      }
    }
  }
  
  // Initialize on mount
  onMounted(() => {
    initTheme()
    watchSystemTheme()
  })
  
  return {
    isDark,
    theme,
    toggleTheme,
    setTheme,
    setSystemTheme,
    initTheme
  }
}

// Export reactive state for use across the app
export { isDark }